import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const SurveySummary = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // 🔹 Load from navigation state OR fallback to localStorage
  const storedCanteen = JSON.parse(localStorage.getItem("selectedCanteen")) || {};
  const storedSurveyType = localStorage.getItem("surveyType") || "";
  
  const [canteen, setCanteen] = useState(state?.canteen || storedCanteen);
  const [surveyType, setSurveyType] = useState(state?.surveyType || storedSurveyType);

  const [surveyData, setSurveyData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 🔹 Load all saved sections from localStorage
    const sections = ["a","b","c","d","e","f","g","h","i","j","k"];
    const allData = {};

    sections.forEach(section => {
      const data = localStorage.getItem(`survey_section_${section}`);
      if (data) allData[section] = JSON.parse(data);
    });

    setSurveyData(allData);
    setLoading(false);
  }, []);

  // 🔹 Submit survey
  const handleSubmitSurvey = () => {
    const allData = {
      canteen,
      surveyType,
      surveyData,
      submissionDate: new Date().toISOString()
    };

    console.log("Submitting survey data:", allData);
    // alert("Survey submitted successfully!");

    // 🔹 Clear storage after submission
    localStorage.removeItem("selectedCanteen");
    localStorage.removeItem("surveyType");
    ["a","b","c","d","e","f","g","h","i","j","k"].forEach(s => {
      localStorage.removeItem(`survey_section_${s}`);
    });
    localStorage.removeItem("completedSections");

    navigate("/survey"); // redirect to survey page
  };

  const handleEditSection = (sectionId) => {
    navigate(`/survey/${surveyType}/section-${sectionId}`, { 
      state: { 
        ...canteen,
        surveyType 
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">Loading survey data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow p-6">
        <h2 className="text-2xl font-bold mb-2">Survey Summary</h2>
        <p className="text-gray-600 mb-6">Review all sections before final submission</p>

        {/* 🔹 Canteen Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-600 font-medium">Canteen Name</p>
            <p className="text-lg font-semibold">{canteen.area_name || "N/A"}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-600 font-medium">Ward ID</p>
            <p className="text-lg font-semibold">{canteen.ward_id || "N/A"}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-purple-600 font-medium">Program Type</p>
            <p className="text-lg font-semibold">{canteen.type_name || canteen.canteen_type || "N/A"}</p>
          </div>
        </div>

        {/* 🔹 Sections */}
        <div className="space-y-4">
          {Object.keys(surveyData).length === 0 && (
            <p className="text-gray-500">No sections completed yet.</p>
          )}

          {Object.keys(surveyData).map(section => (
            <div key={section} className="border p-4 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">
                  Section {section.toUpperCase()}: {getSectionName(section)}
                </h3>
                <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                  Completed
                </span>
              </div>
              <p className="text-sm text-gray-600">
                Submitted on: {surveyData[section]?.timestamp
                  ? new Date(surveyData[section].timestamp).toLocaleString()
                  : "N/A"}
              </p>
              <button
                className="mt-2 text-blue-600 text-sm hover:text-blue-800"
                onClick={() => handleEditSection(section)}
              >
                Edit Section
              </button>
            </div>
          ))}
        </div>

        {/* 🔹 Actions */}
        <div className="mt-8 flex justify-between">
          <button
            className="px-6 py-2 bg-gray-200 rounded hover:bg-gray-300"
            onClick={() => navigate("/survey/sections", { 
              state: { 
                canteen,
                surveyType 
              } 
            })}
          >
            Back to Sections
          </button>
          <button
            className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            onClick={handleSubmitSurvey}
          >
            Submit Complete Survey
          </button>
        </div>
      </div>
    </div>
  );
};

// 🔹 Section Name Mapping
function getSectionName(section) {
  const sectionNames = {
    a: "General Information",
    b: "Food Quality & Quantity",
    c: "Water Supply & Quality",
    d: "Kitchen & Dining Space",
    e: "Safety Arrangements",
    f: "Sanitation & Cleanliness",
    g: "Waste Management",
    h: "Personal Hygiene",
    i: "Food Hygiene",
    j: "IEC & Monitoring",
    k: "Challenges & Suggestions"
  };
  return sectionNames[section] || "Unknown";
}

export default SurveySummary;